'use client'

import React, { useState, useEffect } from 'react'
import { http } from '../../../utils/clientHttp'
import styles from './styles.module.css'
import Text from '../../Text'
import CtaV2 from '../../CtaV2'

function OrderShippingInfoPage({
  backgroundColor,
  textColor,
  title = 'Track Your Order',
  subtitle = 'Enter your order code and postal code to view shipping information',
  orderCodeLabel = 'Order Code',
  postalCodeLabel = 'Postal Code',
  submitButtonText = 'Track Order',
  errorMessage = 'Order not found. Please check your order code and postal code.'
}) {
  // Runtime CMS-driven colours -> inline style (matches former styled `${props}`).
  const colorStyle = {
    backgroundColor: backgroundColor?.value || '#072947',
    color: textColor?.value || '#ffffff'
  }

  const [orderCode, setOrderCode] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [orderInfo, setOrderInfo] = useState(null)

  const fetchOrderInfo = async (code, postal) => {
    if (!code?.trim() || !postal?.trim()) return

    setLoading(true)
    setError(null)
    setOrderInfo(null)

    try {
      const response = await http.get(
        `/api/getOrderShippingInfo?orderCode=${encodeURIComponent(code)}&postalCode=${encodeURIComponent(postal)}`
      )
      setOrderInfo(response.data)
    } catch (err) {
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Auto-fetch if query params are present. Read them CLIENT-SIDE from the URL after
  // mount (this effect never runs during SSR) instead of `next/router` useRouter,
  // which throws under App-Router SSR now that this section renders on the RSC server
  // path (#118 slice 7 — was 500ing /tracking). Behaviour is identical: the prefill
  // is a client-only convenience. Using window.location (not next/navigation's
  // useSearchParams) also avoids the CSR-bailout that hook would force on this page.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const queryOrderCode = params.get('orderCode')
    const queryPostalCode = params.get('postalCode')
    if (queryOrderCode && queryPostalCode) {
      setOrderCode(queryOrderCode)
      setPostalCode(queryPostalCode)
      fetchOrderInfo(queryOrderCode, queryPostalCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    fetchOrderInfo(orderCode, postalCode)
  }

  const handleBack = () => {
    setOrderInfo(null)
    setError(null)
  }

  return (
    <div className={styles.wrapper} style={colorStyle}>
      <div className={styles.contentWrapper}>
        <div className={styles.headerWrapper}>
          <div className={styles.titleWrapper}>
            <Text type="h2">{title}</Text>
          </div>
          <Text type="body-long-1">{subtitle}</Text>
        </div>

        {!orderInfo && (
          <form className={styles.formWrapper} onSubmit={handleSubmit}>
            <div className={styles.inputWrapper}>
              <label className={styles.label} htmlFor="orderCode">{orderCodeLabel}</label>
              <input
                className={styles.input}
                id="orderCode"
                type="text"
                value={orderCode}
                onChange={(e) => setOrderCode(e.target.value)}
                placeholder="e.g. XXXZZZ"
                disabled={loading}
              />
            </div>

            <div className={styles.inputWrapper}>
              <label className={styles.label} htmlFor="postalCode">{postalCodeLabel}</label>
              <input
                className={styles.input}
                id="postalCode"
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="e.g. 12345"
                disabled={loading}
              />
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <div className={styles.ctaWrapper}>
              <CtaV2
                title={submitButtonText}
                type="submit"
                disabled={loading || !orderCode.trim() || !postalCode.trim()}
                isLoading={loading}
                sx={{ width: '100%', height: '48px' }}
              />
            </div>
          </form>
        )}

        {orderInfo && (
          <div className={styles.resultWrapper}>
            <button className={styles.backButton} onClick={handleBack}>
              &larr; Search Another Order
            </button>

            <div className={styles.orderInfoCard}>
              <div className={styles.orderHeader}>
                <div>
                  <Text type="h4">Order Details</Text>
                  <div className={styles.orderCode}>Order: {orderInfo.orderCode}</div>
                </div>
                <div className={styles.orderStatus}>{orderInfo.orderStatus}</div>
              </div>

              {orderInfo.trackingLink && (
                <div style={{ marginBottom: '16px' }}>
                  <a className={styles.trackingLink} href={orderInfo.trackingLink} target="_blank" rel="noopener noreferrer">
                    View Tracking Information &rarr;
                  </a>
                </div>
              )}

              <Text type="h5" style={{ marginBottom: '12px' }}>Items</Text>
              <div className={styles.itemsWrapper}>
                {orderInfo.items && orderInfo.items.map((item, index) => (
                  <div className={styles.itemCard} key={index}>
                    {item.galleryImages && item.galleryImages.length > 0 && (
                      // eslint-disable-next-line @next/next/no-img-element, local-rules/prefer-img-component, local-rules/no-raw-media-src -- runtime backend order-image URL (not resolver-managed); box is fixed 120x120 by CSS
                      <img className={styles.itemImage} src={item.galleryImages[0].src} alt={item.displayName} width={120} height={120} />
                    )}
                    <div className={styles.itemInfo}>
                      <div className={styles.itemName}>
                        {item.displayName}
                        {item.quantity > 1 && ` (x${item.quantity})`}
                      </div>
                                            {item.messages && item.messages.length > 0 && (
                        <div className={styles.itemMessages}>
                          {item.messages.map((message, msgIndex) => (
                            <div className={styles.itemMessage} key={msgIndex}>{message}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default OrderShippingInfoPage
